import { createSelector } from 'reselect';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { StoreState } from '../types/StoreState';
import { ApplePayPaymentMethod } from '../types/PaymentMethods';
import { AsyncStatus } from '../reducers/requestsState';
import { selectTopProps } from './topProps';
import { selectHighestSupportedApplePayVersion, selectApCanMakePayments } from './appState';
import { selectRequestStateApCanMakePayments } from './requestsState';

const isFullfilledOrRejected = (s: AsyncStatus): boolean => {
  return s === 'fulfilled' || s === 'rejected';
};

export const selectApplePayDto = (state: StoreState): ApplePayPaymentMethod => {
  const apay = state.domain.paymentMethods.find((pm) => !!pm[PaymentMethod.ApplePay]);
  return apay as ApplePayPaymentMethod;
};

export const selectApplePayCountryCode = createSelector(
  selectApplePayDto,
  (pm: ApplePayPaymentMethod): string => pm && pm[PaymentMethod.ApplePay].merchantCountry,
);

export const selectApplePayMerchantCapabilities = createSelector(
  selectApplePayDto,
  (pm: ApplePayPaymentMethod) => pm && pm[PaymentMethod.ApplePay].merchantCapabilities,
);

export const selectApplePaySupportedNetworks = createSelector(
  selectApplePayDto,
  (pm: ApplePayPaymentMethod) => pm && pm[PaymentMethod.ApplePay].supportedNetworks,
);

export const selectApplePayBtnColor = createSelector(selectTopProps, (topProps) => {
  // https://jira.wixpress.com/browse/PAY-13432
  return topProps.theme === 'light' ? 'white' : 'black';
});

export const selectApplePayButtonAvailable = createSelector(
  selectApplePayDto,
  selectTopProps,
  selectHighestSupportedApplePayVersion,
  selectRequestStateApCanMakePayments,
  selectApCanMakePayments,
  (pm, topProps, highestSupportedVersion, requestStateCanMakePayments, canMakePayments) => {
    if (!topProps.allowPaymentMethods.includes(PaymentMethod.ApplePay)) {
      return false;
    }
    if (pm && topProps.demoMode) {
      return true;
    }
    if (highestSupportedVersion < 3) {
      // it's too old, not supported
      return false;
    }

    if (Number(topProps.paymentAmount) < 1) {
      // WixPayments min transaction is amount 1$
      return false;
    }
    if (!pm || !isFullfilledOrRejected(requestStateCanMakePayments)) {
      return false;
    }

    return canMakePayments;
  },
);
